/**
 * The job's notes as a log.
 *
 * <p>One timeline per work order, shared by everyone on the job: the
 * advisor's intake note, the mechanic's "left caliper seized", the
 * manager's reply. Each entry is the message, with who wrote it and when
 * beneath, each on its own card so one note is visibly one note — stacked
 * in order, oldest first, the composer pinned under the list (user calls
 * 2026-09-22: "note style … message, who and when", not bubbles; "a visual
 * hint and separator that group one note from the others"). While the
 * draft carries Markdown a live preview renders under the field, as in any
 * editor with one. Bodies render as Markdown (GFM) so a
 * manager can write a list or bold the one thing that matters — raw HTML
 * is dropped, links open in a new tab, a single line break inside plain
 * text is a line break; list items, headings, quotes, tables and fenced
 * code keep their own rules. Enter is a new line — a note is often a list,
 * and a mechanic hitting Enter mid-thought must not fire it off; the
 * button sends, Ctrl/Cmd+Enter as a shortcut.
 *
 * <p>A sent note is final for its author on the floor and editable by a
 * manager: {@link NoteThreadProps.notesEditable} says who may rewrite or
 * remove — nobody, the author, or anybody. Presentational: the app maps
 * its wire shape onto {@link ThreadNote}, supplies the labels, and owns
 * the mutations and the delete confirm (`onDelete` is a request, not an
 * act).
 */
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Pencil, Send, Trash2 } from 'lucide-react'
import { cn } from '../../utils/cn'
import { InlineSpinner } from '../InlineSpinner'

export interface ThreadNote {
  readonly id: string
  readonly body: string
  /** Preformatted time ("10:42", "21.09. 18:19") — each app formats its own wire shape. */
  readonly stamp: string
  readonly authorName: string | null
  /** Written by the reader: sits on the right and may be edited or removed. */
  readonly mine: boolean
}

export interface NoteThreadLabels {
  /** Section name and the count's caption — also the input's accessible name. */
  readonly title: string
  readonly placeholder: string
  readonly send: string
  readonly edit: string
  readonly delete: string
  readonly save: string
  readonly cancel: string
  /** Author label on the reader's own notes. */
  readonly you: string
  readonly empty: string
  /** Shown under the composer when a write is refused or the network drops. */
  readonly error: string
  /** Caption of the live rendering under the composer while the draft carries Markdown. */
  readonly preview: string
}

/** Who may rewrite or remove a sent note. */
export type NotesEditable = 'none' | 'own' | 'all'

export interface NoteThreadProps {
  readonly notes: readonly ThreadNote[]
  /**
   * Nobody (the technician app — a sent note is final), the author, or
   * anybody (the manager, who moderates the whole log). Default: nobody.
   */
  readonly notesEditable?: NotesEditable
  /** The page already names the section (a numbered heading, say): keep the name for assistive tech, skip the caption. */
  readonly hideTitle?: boolean
  /** Bounds the scrolling list — defaults to a chat-window height. */
  readonly listClassName?: string
  /**
   * Called once with the scrolling element that DRAWS the notes. For a
   * host that needs to know the log has actually been on screen —
   * management marks the job's notes read off it — rather than that the
   * component mounted. Omit and nothing observes anything.
   */
  readonly onListElement?: (el: HTMLElement | null) => void
  readonly labels: NoteThreadLabels
  /** Resolves once the note is stored; the draft clears then, and stays on failure. */
  readonly onAdd: (body: string) => unknown
  readonly onEdit?: (id: string, body: string) => unknown
  /** Asked, not done — the app confirms and deletes. */
  readonly onDelete?: (id: string) => void
  readonly pending?: boolean
  /**
   * The note the host is deleting right now, so it can show as going
   * rather than sit there unchanged until the refetch lands. The delete
   * runs in the host (after its confirm), so only the host knows.
   */
  readonly busyNoteId?: string | null
  /** The job is closed to writes (cancelled, invoiced): no composer, no edit or delete. */
  readonly readOnly?: boolean
  readonly className?: string
  /** Rendered inside the section, after the thread — a confirm dialog, typically. */
  readonly children?: ReactNode
}

const MAX_ROWS = 6

/** Bullets, headings, quotes and table rows always open a block of their own. */
const BLOCK_LINE = /^\s*(?:[-*+]\s|#{1,6}\s|>|\|)/
const ORDERED = /^\s*(\d+)[.)]\s/
const LIST_LINE = /^\s*(?:[-*+]\s|\d+[.)]\s)/
const FENCE = /^\s*(?:```|~~~)/

/**
 * Whether Markdown reads this line as its own block. An ordered item only
 * does when it starts at 1 or continues a list — "2. attempt failed" after
 * a plain sentence is just a sentence, and needs its break.
 */
function isBlockLine(line: string, prev: string | undefined): boolean {
  if (BLOCK_LINE.test(line)) return true
  const ordered = ORDERED.exec(line)
  if (!ordered) return false
  return ordered[1] === '1' || (prev !== undefined && LIST_LINE.test(prev))
}

/**
 * A single newline between two plain lines is a line break in a chat;
 * Markdown wants two trailing spaces for that. Only plain text gets them:
 * a break glued onto a list item, a heading or a table row changes how
 * the next line parses, and inside a fenced block nothing is touched.
 */
export function chatToMarkdown(body: string): string {
  const lines = body.split('\n')
  let inFence = false
  return lines
    .map((line, i) => {
      if (FENCE.test(line)) {
        inFence = !inFence
        return line
      }
      if (inFence) return line
      const next = lines[i + 1]
      const prev = i > 0 ? lines[i - 1] : undefined
      if (next === undefined || next.trim() === '' || line.trim() === '') return line
      if (isBlockLine(line, prev) || isBlockLine(next, line) || FENCE.test(next)) return line
      return line.replace(/\s*$/, '  ')
    })
    .join('\n')
}

const BODY_CLASS =
  'text-[13px] leading-snug break-words [&_p]:m-0 [&_p+p]:mt-1.5 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4 ' +
  '[&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_li+li]:mt-0.5 [&_a]:underline [&_strong]:font-bold ' +
  '[&_code]:rounded-sm [&_code]:bg-glass-fill [&_code]:px-1 [&_code]:font-mono [&_code]:text-[12px] ' +
  '[&_pre]:my-1 [&_pre]:overflow-x-auto [&_pre]:rounded-sm [&_pre]:bg-glass-fill [&_pre]:p-2 ' +
  '[&_blockquote]:border-l-2 [&_blockquote]:border-glass-border [&_blockquote]:pl-2 [&_blockquote]:text-glass-text-muted ' +
  '[&_table]:my-1 [&_table]:text-[12px] [&_th]:pr-2 [&_th]:text-left [&_td]:pr-2 [&_h1]:text-[14px] [&_h2]:text-[14px] [&_h3]:text-[13px] [&_h1]:font-bold [&_h2]:font-bold [&_h3]:font-bold ' +
  '[&_img]:my-1 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-sm'

function NoteBody({ body }: { readonly body: string }) {
  return (
    <div className={BODY_CLASS}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        components={{
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noreferrer noopener">
              {children}
            </a>
          ),
        }}
      >
        {chatToMarkdown(body)}
      </Markdown>
    </div>
  )
}

const ICON_BUTTON =
  'grid h-6 w-6 place-items-center rounded-control text-glass-text-muted transition hover:bg-glass-fill-strong hover:text-glass-text'
// `block`: an inline textarea leaves a baseline gap beneath itself, and the
// send glyph anchored to the wrapper's bottom then sat a few pixels low
// (user report 2026-09-22).
const TEXTAREA =
  'block w-full resize-none rounded-md border border-glass-border bg-glass-fill-strong px-3 py-2 text-[13px] leading-5 text-glass-text placeholder:text-glass-text-muted focus:border-glass-border-strong focus:outline-none'
/** A glyph in the field's corner, not a disc: accent when there is something to send, grey when not. */
const SEND_BUTTON =
  'absolute bottom-[5px] right-[6px] grid h-7 w-7 place-items-center rounded-control text-accent transition hover:bg-accent/10 active:scale-[0.97] disabled:cursor-default disabled:text-glass-text-muted disabled:hover:bg-transparent'
const TEXT_BUTTON = 'rounded-control px-3 py-1.5 text-[12px] font-bold transition'

function rowsFor(text: string): number {
  return Math.min(MAX_ROWS, Math.max(1, text.split('\n').length))
}

/** Anything Markdown would render differently from the raw text — then a preview earns its place. */
const MARKDOWN_HINT = /(\*\*|__|~~|`|\[[^\]]+\]\(|^\s*[-*+]\s|^\s*\d+[.)]\s|^#{1,6}\s|^\s*>|\|)/m
export function looksLikeMarkdown(text: string): boolean {
  return MARKDOWN_HINT.test(text)
}

/** The draft as it will read once sent — shown while typing, like any editor with a preview. */
function DraftPreview({ body, label }: { readonly body: string; readonly label: string }) {
  return (
    <div className="mt-1.5 rounded-md border border-dashed border-glass-border px-2.5 py-1.5" data-testid="note-preview">
      <div className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-glass-text-muted">{label}</div>
      <NoteBody body={body} />
    </div>
  )
}

export function NoteThread({
  notes,
  labels,
  onAdd,
  onEdit,
  onDelete,
  pending = false,
  busyNoteId = null,
  readOnly = false,
  notesEditable = 'none',
  hideTitle = false,
  listClassName = 'max-h-[22rem]',
  onListElement,
  className,
  children,
}: NoteThreadProps) {
  const [draft, setDraft] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [savingEdit, setSavingEdit] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Newest at the bottom, and the bottom in view: a note that just landed
  // is the one the reader came for.
  useEffect(() => {
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [notes.length])

  // Handed out once the list is in the DOM, and taken back on unmount, so
  // a host observing it never holds a detached node. An effect rather
  // than composing into `ref`: an inline callback ref fires null/element
  // on every render, which would thrash a consumer storing it in state.
  useEffect(() => {
    onListElement?.(listRef.current)
    return () => onListElement?.(null)
  }, [onListElement])

  const send = async () => {
    const body = draft.trim()
    if (!body || pending || sending) return
    setSending(true)
    setError(null)
    try {
      await onAdd(body)
      setDraft('')
    } catch {
      // The draft stays, and the reader is told: a refused write (the job
      // is closed, a colleague's note) or a dropped connection.
      setError(labels.error)
    } finally {
      setSending(false)
    }
  }

  // Enter stays a new line; only the button (or Ctrl/Cmd+Enter) sends.
  const onDraftKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void send()
    }
  }

  const startEdit = (n: ThreadNote) => {
    setEditingId(n.id)
    setEditDraft(n.body)
  }
  const cancelEdit = () => setEditingId(null)
  const saveEdit = async () => {
    const body = editDraft.trim()
    const id = editingId
    if (!id || savingEdit) return
    setError(null)
    setSavingEdit(true)
    try {
      if (body && onEdit) await onEdit(id, body)
      setEditingId(null)
    } catch {
      // The editor stays open with the text in it.
      setError(labels.error)
    } finally {
      setSavingEdit(false)
    }
  }
  const onEditKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void saveEdit()
    } else if (e.key === 'Escape') {
      // Ours, not the host's: an overlay listening on the document would
      // otherwise close the whole panel on the keystroke meant for this box.
      e.stopPropagation()
      cancelEdit()
    }
  }

  const busy = pending || sending
  const canManage = (n: ThreadNote) =>
    !readOnly && notesEditable !== 'none' && (notesEditable === 'all' || n.mine)

  return (
    <section
      aria-label={labels.title}
      className={cn(
        'flex min-h-0 flex-col rounded-md border border-glass-border bg-glass-fill text-glass-text',
        className,
      )}
    >
      {!hideTitle && (
        <div className="px-3 pt-2.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-glass-text-muted">
          {labels.title} ({notes.length})
        </div>
      )}

      <div
        ref={listRef}
        className={cn('min-h-0 flex-1 overflow-y-auto px-3 py-2.5', listClassName)}
        data-testid="note-thread-list"
      >
        {notes.length === 0 ? (
          <p className="text-[12px] text-glass-text-muted">{labels.empty}</p>
        ) : (
          <ol className="flex flex-col gap-1.5">
            {notes.map((n) => (
              <li
                key={n.id}
                data-testid={n.mine ? 'note-mine' : 'note-theirs'}
                aria-busy={busyNoteId === n.id || undefined}
                className={cn(
                  'rounded-md border border-glass-border bg-glass-fill-strong px-2.5 py-2 transition-opacity',
                  busyNoteId === n.id && 'opacity-50',
                )}
              >
                {editingId === n.id ? (
                  <div className="flex flex-col gap-1.5">
                    <textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      onKeyDown={onEditKey}
                      rows={rowsFor(editDraft)}
                      aria-label={labels.edit}
                      className={TEXTAREA}
                      readOnly={savingEdit}
                      autoFocus
                    />
                    {looksLikeMarkdown(editDraft) && <DraftPreview body={editDraft} label={labels.preview} />}
                    {error && <p role="alert" className="text-[11.5px] text-critical">{error}</p>}
                    <div className="flex justify-end gap-1.5">
                      <button type="button" onClick={cancelEdit} disabled={savingEdit} className={cn(TEXT_BUTTON, 'text-glass-text-muted hover:bg-glass-fill-strong disabled:opacity-50')}>
                        {labels.cancel}
                      </button>
                      <button
                        type="button"
                        onClick={() => void saveEdit()}
                        disabled={savingEdit}
                        aria-busy={savingEdit || undefined}
                        className={cn(TEXT_BUTTON, 'inline-flex items-center gap-1.5 bg-accent text-white hover:opacity-90 disabled:opacity-80')}
                      >
                        {savingEdit && <InlineSpinner size={12} />}
                        {labels.save}
                      </button>
                    </div>
                  </div>
                ) : (
                  <NoteBody body={n.body} />
                )}
                {/* Who and when, beneath the message and on the right — the note's
                    signature line (user call 2026-09-22). */}
                <div className="mt-1 flex items-center justify-end gap-1.5 text-[10.5px] text-glass-text-muted">
                  <span className="font-semibold">{n.mine ? labels.you : (n.authorName ?? '—')}</span>
                  <span aria-hidden="true">·</span>
                  <span className="font-mono">{n.stamp}</span>
                  {busyNoteId === n.id && <InlineSpinner size={11} className="ml-1" />}
                  {canManage(n) && editingId !== n.id && busyNoteId !== n.id && (onEdit || onDelete) && (
                    <span className="ml-1 flex items-center gap-0.5">
                      {onEdit && (
                        <button type="button" aria-label={labels.edit} onClick={() => startEdit(n)} className={ICON_BUTTON}>
                          <Pencil size={12} strokeWidth={2.25} aria-hidden="true" />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          aria-label={labels.delete}
                          onClick={() => onDelete(n.id)}
                          className={cn(ICON_BUTTON, 'hover:bg-accent/10 hover:text-accent')}
                        >
                          <Trash2 size={12} strokeWidth={2.25} aria-hidden="true" />
                        </button>
                      )}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {!readOnly && (
        <div className="border-t border-glass-border px-3 py-2.5">
          <div className="relative">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onDraftKey}
              rows={rowsFor(draft)}
              placeholder={labels.placeholder}
              aria-label={labels.title}
              // Held while it sends: the text is on its way, and typing
              // into it now would be lost when it clears on success.
              readOnly={busy}
              className={cn(TEXTAREA, 'pr-10')}
            />
            <button
              type="button"
              aria-label={labels.send}
              title={labels.send}
              onClick={() => void send()}
              disabled={!draft.trim() || busy}
              aria-busy={busy || undefined}
              className={SEND_BUTTON}
            >
              {busy ? <InlineSpinner size={16} /> : <Send size={16} strokeWidth={2} aria-hidden="true" />}
            </button>
          </div>
          {looksLikeMarkdown(draft) && <DraftPreview body={draft} label={labels.preview} />}
          {error && editingId === null && (
            <p role="alert" className="mt-1 text-[11.5px] text-critical">{error}</p>
          )}
        </div>
      )}

      {children}
    </section>
  )
}
